import type { OnboardingSlide } from './onboardingStorage';

/**
 * Référentiel des slides d'onboarding pour les 5 modules (MVP2.5 T7).
 *
 * Séparé du composant OnboardingModal pour respecter la règle
 * `react-refresh/only-export-components` ESLint (le composant React ne doit
 * pas co-habiter avec des constantes consommées par d'autres composants).
 *
 * Le texte suit DESIGN §6 :
 *   - voix directe, technique mais accessible,
 *   - tutoiement,
 *   - reconnaissance data-based,
 *   - pas d'emoji caractère (utilise des spans/typographie monospace pour
 *     les visuels).
 */

const BOARD_VISUAL = (
  <span>
    {`   8 │ r n b q k b n r
   7 │ p p p p p p p p
   6 │ . . . . . . . .
   5 │ . . . . . . . .
   4 │ . . . . . . . .
   3 │ . . . . . . . .
   2 │ P P P P P P P P
   1 │ R N B Q K B N R
     └─────────────────
       a b c d e f g h`}
  </span>
);

const COCKPIT_VISUAL = (
  <span>
    {`  ┌──────────────────────┐
  │   Aujourd'hui · 45 min │
  │   [ ◑ 60 ] / 100 pts   │
  │                        │
  │   • Ouvertures   30pts │
  │   • Tactiques    25pts │
  │   • Finales      25pts │
  │   • Autopsie     20pts │
  └──────────────────────┘`}
  </span>
);

export const MODULE_SLIDES: Record<string, OnboardingSlide[]> = {
  'mission-control': [
    {
      title: 'Ton cockpit',
      visual: COCKPIT_VISUAL,
      body: (
        <>
          Ici tu vois ce qu'il faut faire aujourd'hui — 4 tâches max : ouvertures,
          tactiques, finales, autopsie. Le cercle au centre te montre ta progression
          du jour.
        </>
      ),
    },
    {
      title: 'Les points (XP)',
      body: (
        <>
          Chaque exercice rapporte des points.{' '}
          <strong className="text-acid-500">100 pts/jour</strong> = objectif. Pas
          de stress : si tu manques un jour, rien ne se perd. Le détail du barème
          est en bas du cockpit (« Comment je gagne des points ? »).
        </>
      ),
    },
  ],
  'opening-lab': [
    {
      title: '5 modes d\'entraînement',
      visual: BOARD_VISUAL,
      body: (
        <>
          Tu choisis ton répertoire (Caro-Kann, Italian, ...) puis ton mode :{' '}
          <strong className="text-violet-500">
            Mémoire, Plan, Riposte, Tiens bon, Miroir
          </strong>
          . Chaque mode travaille un angle différent.
        </>
      ),
    },
    {
      title: 'Pourquoi varier',
      body: (
        <>
          Mémoriser sans comprendre = piège. Les 5 modes te forcent à savoir{' '}
          <strong>POURQUOI</strong> tu joues un coup, pas juste lequel. C'est la
          différence entre un joueur 1700 et un joueur 2000.
        </>
      ),
    },
  ],
  'tactics-dojo': [
    {
      title: 'Les motifs tactiques',
      body: (
        <>
          Clouage, enfilade, fourchette, déviation...{' '}
          <strong className="text-cyan-500">13 motifs</strong> à reconnaître à
          l'instinct. Plus tu en vois en exercice, plus tu en repères en partie
          réelle.
        </>
      ),
    },
    {
      title: 'Sous pression',
      body: (
        <>
          L'objectif n'est pas juste de réussir — c'est de voir{' '}
          <strong>vite</strong>. Un timer s'affiche pour t'entraîner à décider
          en moins de 30 secondes, comme en blitz.
        </>
      ),
    },
  ],
  'endgame-forge': [
    {
      title: 'Convertir l\'avantage',
      body: (
        <>
          Une finale = peu de pièces, beaucoup de précision. Tu joues la séquence
          prouvée par Stockfish ou par la tablebase, coup par coup. Pas de hasard,
          que de la technique.
        </>
      ),
    },
    {
      title: 'Trois objectifs',
      body: (
        <>
          Selon la position :{' '}
          <strong className="text-amber-500">Gagner</strong> (tu as l'avantage),{' '}
          <strong className="text-ink-300">Annuler</strong> (égalité possible),{' '}
          <strong className="text-paper-50">Défendre</strong> (tu es en infériorité
          mais tu peux sauver la nulle).
        </>
      ),
    },
  ],
  'game-autopsy': [
    {
      title: 'Le diagnostic',
      body: (
        <>
          Stockfish analyse ta partie chess.com ou Lichess coup par coup, à
          profondeur 16. ~90 secondes pour 40 coups. Tu vois exactement où tu as
          décroché et de combien (en centipawns).
        </>
      ),
    },
    {
      title: 'Tes erreurs deviennent des cartes',
      body: (
        <>
          Chaque <strong className="text-coral-500">gaffe</strong> importante crée
          automatiquement une carte de révision dans Opening Lab, à revoir demain.
          L'erreur devient une data.
        </>
      ),
    },
  ],
};
